import { useState, useRef, useEffect } from "react";
import { useGoogleMap } from "@react-google-maps/api";
import Supercluster from "supercluster";
import classnames from "../../../../utils/classnames.js";
import { DEFAULT_CLUSTER_MAX_ZOOM, DEFAULT_CLUSTER_RADIUS } from "./constants.js";
const createClusterInstance = (pins) => {
  const points = pins.map((pin) => ({
    type: "Feature",
    properties: { pinId: pin.id },
    geometry: {
      type: "Point",
      coordinates: [pin.longitude, pin.latitude]
    }
  }));
  const cluster = new Supercluster({
    radius: DEFAULT_CLUSTER_RADIUS,
    maxZoom: DEFAULT_CLUSTER_MAX_ZOOM
  });
  cluster.load(points);
  return cluster;
};
const getClustersInBounds = (cluster, map) => {
  const bounds = map.getBounds();
  const zoom = map.getZoom() ?? 10;
  if (!bounds) {
    return [];
  }
  const ne = bounds.getNorthEast();
  const sw = bounds.getSouthWest();
  const padding = 0.2;
  const lngDiff = ne.lng() - sw.lng();
  const latDiff = ne.lat() - sw.lat();
  return cluster.getClusters(
    [
      sw.lng() - lngDiff * padding,
      sw.lat() - latDiff * padding,
      ne.lng() + lngDiff * padding,
      ne.lat() + latDiff * padding
    ],
    zoom
  );
};
const Clusterer = ({
  pins,
  clusterImage,
  clusterHoverImage,
  clusterSize,
  clusterHoverSize = clusterSize,
  onUpdateClusterPins
}) => {
  const map = useGoogleMap();
  const [clusterInstance, setClusterInstance] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);
  const markerMapRef = useRef(/* @__PURE__ */ new Map());
  useEffect(() => {
    if (!map || !pins.length) {
      return;
    }
    const cluster = createClusterInstance(pins);
    setClusterInstance(cluster);
  }, [map, pins]);
  useEffect(() => {
    onUpdateClusterPins == null ? void 0 : onUpdateClusterPins([], false);
  }, []);
  useEffect(() => {
    if (!map || !clusterInstance) {
      return;
    }
    const update = () => {
      const newClusters = getClustersInBounds(clusterInstance, map);
      const clusteredPinIds = [];
      const existingMarkers = markerMapRef.current;
      const newMarkerMap = /* @__PURE__ */ new Map();
      newClusters.forEach((cluster) => {
        if (!cluster.properties.cluster) {
          return;
        }
        const clusterId = cluster.id;
        const isHovered = hoveredId === clusterId;
        const image = isHovered && clusterHoverImage ? clusterHoverImage : clusterImage;
        const size = isHovered ? clusterHoverSize : clusterSize;
        const count = cluster.properties.point_count_abbreviated ?? 1;
        const position = {
          lat: cluster.geometry.coordinates[1],
          lng: cluster.geometry.coordinates[0]
        };
        const children = clusterInstance.getLeaves(clusterId, Infinity);
        children.forEach((child) => {
          if (child.properties.pinId) {
            clusteredPinIds.push(child.properties.pinId);
          }
        });
        let marker = existingMarkers.get(clusterId);
        if (marker) {
          marker.setPosition(position);
          marker.setIcon({
            url: image,
            scaledSize: new google.maps.Size(size[0], size[1]),
            anchor: new google.maps.Point(size[0] / 2, size[1] / 2)
          });
          marker.setLabel({
            text: `${count}`,
            className: classnames([
              "perxis-auto-map__cluster",
              {
                "perxis-auto-map__cluster-hover": isHovered
              }
            ])
          });
        } else {
          marker = new google.maps.Marker({
            position,
            map,
            icon: {
              url: image,
              scaledSize: new google.maps.Size(size[0], size[1]),
              anchor: new google.maps.Point(size[0] / 2, size[1] / 2)
            },
            label: {
              text: `${count}`,
              className: classnames("perxis-auto-map__cluster")
            }
          });
          marker.addListener("click", () => {
            const bounds = new google.maps.LatLngBounds();
            children.forEach((child) => {
              const pin = pins.find((p) => p.id === child.properties.pinId);
              if (pin) {
                bounds.extend(new google.maps.LatLng(pin.latitude, pin.longitude));
              }
            });
            map.fitBounds(bounds, { top: 60, bottom: 60, left: 60, right: 60 });
          });
          marker.addListener("mouseover", () => setHoveredId(clusterId));
          marker.addListener("mouseout", () => setHoveredId(null));
        }
        marker.setMap(map);
        newMarkerMap.set(clusterId, marker);
      });
      existingMarkers.forEach((marker, id) => {
        if (!newMarkerMap.has(id)) {
          marker.setMap(null);
        }
      });
      markerMapRef.current = newMarkerMap;
      onUpdateClusterPins == null ? void 0 : onUpdateClusterPins(clusteredPinIds, true);
    };
    update();
    const listener = map.addListener("idle", update);
    return () => listener.remove();
  }, [
    map,
    clusterInstance,
    hoveredId,
    pins,
    clusterImage,
    clusterHoverImage,
    clusterSize,
    clusterHoverSize,
    onUpdateClusterPins
  ]);
  return null;
};
export {
  createClusterInstance,
  Clusterer as default,
  getClustersInBounds
};
//# sourceMappingURL=Clusterer.js.map
